
import React, { useState, useRef, useEffect } from 'react';
import { PrayerRequest, PrayerStatus } from '../types';
import { CheckCircleIcon, ClockIcon, BookOpenIcon, MoreIcon, EditIcon, DeleteIcon, ArrowPathIcon } from './icons';

interface PrayerCardProps {
  prayer: PrayerRequest;
  onEdit: (prayer: PrayerRequest) => void;
  onDelete: (prayer: PrayerRequest) => void;
  onRecommendVerse: (prayer: PrayerRequest) => void;
}

const PrayerCard: React.FC<PrayerCardProps> = ({ prayer, onEdit, onDelete, onRecommendVerse }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const statusInfo = {
    [PrayerStatus.CURRENT]: {
      icon: <ClockIcon className="w-5 h-5 text-yellow-500" />,
      borderColor: 'border-yellow-300 dark:border-yellow-600',
      textColor: 'text-yellow-600 dark:text-yellow-400',
    },
    [PrayerStatus.ONGOING]: {
      icon: <ArrowPathIcon className="w-5 h-5 text-blue-500" />,
      borderColor: 'border-blue-300 dark:border-blue-600',
      textColor: 'text-blue-600 dark:text-blue-400',
    },
    [PrayerStatus.ANSWERED]: {
      icon: <CheckCircleIcon className="w-5 h-5 text-green-500" />,
      borderColor: 'border-green-300 dark:border-green-700',
      textColor: 'text-green-600 dark:text-green-400',
    },
  }[prayer.status];
    
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`bg-white dark:bg-gray-800 shadow-lg dark:shadow-black/20 rounded-xl border-l-8 ${statusInfo.borderColor} p-5 transition-transform duration-300 hover:scale-105 flex flex-col`}>
      <div className="flex justify-between items-start mb-2">
        <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex-1 pr-2">{prayer.title}</h3>
        <div className="relative" ref={menuRef}>
            <button onClick={() => setMenuOpen(prev => !prev)} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded-full">
                <MoreIcon className="w-6 h-6" />
            </button>
            {menuOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-700 rounded-md shadow-xl z-20 py-1 border border-gray-200 dark:border-gray-600">
                    <button onClick={() => { onEdit(prayer); setMenuOpen(false); }} className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600">
                        <EditIcon className="w-4 h-4"/> Edit
                    </button>
                    <button onClick={() => { onRecommendVerse(prayer); setMenuOpen(false); }} className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-600">
                        <BookOpenIcon className="w-4 h-4"/> Get Verse
                    </button>
                    <button onClick={() => { onDelete(prayer); setMenuOpen(false); }} className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/40">
                        <DeleteIcon className="w-4 h-4"/> Delete
                    </button>
                </div>
            )}
        </div>
      </div>
      <div className="flex-grow">
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            For <span className="font-semibold">{prayer.requestor}</span> on {new Date(prayer.date).toLocaleDateString()}
        </p>
        <p className="text-gray-700 dark:text-gray-300 mb-4 whitespace-pre-wrap">{prayer.details}</p>
        
        {prayer.status === PrayerStatus.ANSWERED && prayer.answeredDetails && (
            <div className="bg-green-50 dark:bg-green-900/40 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-4">
                <div className="flex items-center gap-2 mb-2">
                    <CheckCircleIcon className="w-5 h-5 text-green-600 dark:text-green-400"/>
                    <h4 className="font-semibold text-green-800 dark:text-green-200">Answered</h4>
                </div>
                <p className="text-sm text-green-700 dark:text-green-300">{prayer.answeredDetails}</p>
            </div>
        )}

        {prayer.bibleVerseRecommendation && (
            <div className="bg-blue-50 dark:bg-blue-900/40 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-2">
                    <BookOpenIcon className="w-5 h-5 text-blue-600 dark:text-blue-400"/>
                    <h4 className="font-semibold text-blue-800 dark:text-blue-200">Recommended Scripture</h4>
                </div>
                <p className="text-sm text-blue-700 dark:text-blue-300 italic">"{prayer.bibleVerseRecommendation}"</p>
            </div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-end gap-2 text-sm font-medium">
        {statusInfo.icon}
        <span className={statusInfo.textColor}>
          {prayer.status}
        </span>
      </div>
    </div>
  );
};

export default PrayerCard;